import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { DEMO_QA } from "@/data/demoQA";
import { ArrowRight, Sparkles } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import FreeTenantHelp from "@/components/FreeTenantHelp";

/**
 * QAIndex. public, indexable landing page listing every Q&A.
 *
 * Data sources merged:
 * - Static seed entries from src/data/demoQA.js (always present, ship instantly)
 * - DB-stored entries from GET /api/qa/list (admin-published from the FB workflow)
 *
 * Static entries are deduped against db slugs. db wins (allows admin to
 * replace a seed entry with a tweaked version).
 */
export default function QAIndex() {
 const [adminItems, setAdminItems] = useState([]);

 useEffect(() => {
 api.get("/qa/list").then(({ data }) => setAdminItems(data.items || [])).catch(() => {/* non-fatal */});
 }, []);

 const adminSlugs = new Set(adminItems.map(q => q.slug));
 const staticItems = DEMO_QA.filter(q => !adminSlugs.has(q.slug));
 // Newest admin entries first, then static seed entries.
 const items = [...adminItems,...staticItems];

 return (
 <div className="min-h-screen paper-grain" data-testid="qa-index-page">
 <Navbar />
 <div className="max-w-5xl mx-auto px-6 sm:px-8 py-14">
 <div className="text-center mb-10">
 <div className="label-mono mb-3 text-[#1B6C62]">Real tenant questions · Real Compass answers</div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">Tenant Q&A library</h1>
 <p className="text-[#333] font-serif max-w-2xl mx-auto leading-relaxed">
 Actual scenarios Australian renters have shared in tenant support groups, answered by Compass with the right Act sections, next steps and free-help phone numbers. Use these for your own situation, or share with a friend.
 </p>
 </div>

 <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 mb-14">
 {items.map(qa => (
 <Link
 key={qa.slug}
 to={`/q/${qa.slug}`}
 className="card bg-white hover:border-[#1B6C62] transition-all duration-200 group shadow-[0_10px_30px_-18px_rgba(13,43,82,0.22),0_2px_6px_-2px_rgba(13,43,82,0.05)] hover:shadow-[0_18px_38px_-18px_rgba(13,43,82,0.28),0_4px_10px_-2px_rgba(13,43,82,0.08)] hover:-translate-y-0.5"
 data-testid={`qa-card-${qa.slug}`}
 >
 <div className="flex items-center gap-2 mb-2">
 <span className="label-mono text-[#1B6C62]">{qa.state === "ALL" ? "AU" : qa.state}</span>
 </div>
 <h2 className="font-serif text-lg text-[#0D2B52] mb-2 leading-snug group-hover:text-[#1B6C62] transition-colors">
 {qa.title}
 </h2>
 <p className="text-sm text-[#6B7280] font-serif leading-snug mb-3">{qa.teaser}</p>
 <div className="text-xs text-[#1B6C62] inline-flex items-center gap-1">
 Read answer <ArrowRight size={11} strokeWidth={1.75} />
 </div>
 </Link>
 ))}
 </div>

 <div className="card border-2 border-[#1B6C62] bg-[#E6F5F2]/30 text-center">
 <div className="flex items-center justify-center gap-2 mb-2">
 <Sparkles size={18} strokeWidth={1.75} className="text-[#1B6C62]" />
 <div className="label-mono text-[#1B6C62]">Don&apos;t see your question?</div>
 </div>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Ask Compass about your tenancy</h2>
 <p className="text-[#1A1A1A] font-serif max-w-xl mx-auto mb-5 leading-relaxed">
 Compass knows every Australian state&apos;s Residential Tenancies Act. Upload your lease and ask it anything, including how to draft and send the letter you need.
 </p>
 <div className="flex justify-center flex-wrap gap-3">
 <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="qa-index-signup">
 Start free <ArrowRight size={13} strokeWidth={1.75} />
 </Link>
 <Link to="/ask" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="qa-index-brain">
 Try the free Brain
 </Link>
 <Link to="/pricing" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="qa-index-pricing">
 See plans
 </Link>
 </div>
 </div>
 </div>

 {/* Free state-run tenant services. Sits at the bottom of the Q&A index
     so someone whose question wasn't answered still walks away with the
     authoritative free option for their state. Placed as a full-width
     sibling to the main content so its 4-column grid has room to breathe. */}
 <div className="max-w-5xl mx-auto px-6 sm:px-8">
 <FreeTenantHelp variant="full" />
 </div>
 
      <SiteFooter />
      </div>
 );
}
